/** @jsxImportSource @emotion/react */
import * as R from 'ramda';
import LabeledData from "../../../../forms/elements/LabeledData";
import { getResource } from "../../../../language/resources/getResource";
import { fullName } from "../../../module";
import { FNF_PAGES, fnfIsHotlisted, getErroredEntitlementShares } from "./module";
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { GuestDetails } from '../../../guestDetails';
import { Entitlement, EntitlementShare } from '../../models';
import { HistoricalRedemption } from '../historicalRedemption';
import { getGuestHotlists, isActive, isDeferred } from '../../module';
import { IAppState } from '../../../../core/redux/IAppState';
import { getAllAvailableDays, getAllBlackoutDates, getAllDays, getUsedDays } from '../module';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { getError, getLoading } from '../../../../core/redux/api/module';
import {css} from '@emotion/react';
import { Loader } from '../../../../forms/elements/loader/Loader';
import ErrorView from '../../../../forms/ErrorView';
import { isNilOrEmpty } from '../../../../language/module';
import { apostrophize } from '../../../../language/strings';
import { RedemptionDetails } from '../RedemptionDetails';
import { FnFEntitlementsTable } from './FnFEntitlementsTable';
import { COLUMN_SPACING } from '../../../../forms/styles';
import { FnFRedeemDays } from './FnFRedeemDays';
import { LargeText } from '../../../../styles/styles';
import { RedemptionIdForm } from '../RedemptionIdForm';
import checkmark from '../../../../assets/icons/checkCircleFilled.svg';
import { Redemption } from '../redemption';
import { redeemEntitlementAction } from '../redeemEntitlementAction';
import { submitRedemptionLocalRefAction } from '../submitRedemptionLocalRefAction';
import { ApiError } from '../../../../core/redux/api/apiError';
import { clearApiErrorAction } from '../../../../core/redux/api/clearApiErrorAction';
import { BlackoutDateMetadata } from '../../../../entitlementTypesMetadata/entitlementTypeMetadata';
import { getFnFBlackoutDateMetadataAction } from './getFnFBlackoutDateMetadataAction';
import { DatePickerDateWithMessage } from '../../../../forms/elements/models';
import { DispatchType } from '../../../..';
import { breakerHeldUntil, shownReleaseTime } from '../../../../core/redux/api/circuitBreaker/module';
import { useTimeAhead } from '../../../../language/hooks/useTimeAhead';
import { formatTime } from '../../../../language/time';
import { StyledBoxedMessage } from '../../../../forms/elements/boxedMessage/StyledBoxedMessage';

const styles = {
  redemptionComplete: css`
    display: flex !important;
    align-items: center;
    justify-content: center;
    ${LargeText};
    font-weight: bold;
  `,
  fnfLoader: css`
    align-self: center;
    height: 120px;
    width: 120px;
    margin: 10px 0 !important; /* Fill space of form we're loading data for */
`};


type Props = {
    guest: GuestDetails;
    entitlements: Entitlement[];
    isLoadingEntitlements: boolean;
    redemptionHistory: HistoricalRedemption[];
    entitlementShares: EntitlementShare[];
    isLoadingEntitlementShares: boolean;
    getFnFEntitlements: () => void;
    getEntitlementShares: () => void;
    setDisableClickoffAndKeypressModal: (disable: boolean) => void;
  }

export const FnFRedeemTab = (props: Props ) => {
  const dispatch = useDispatch<DispatchType>();
  const selectedResortAbbreviation = useSelector((state: IAppState) => state.selectedResort.resortAbbreviation);
  const hotlists = useSelector((state: IAppState) => getGuestHotlists(state), shallowEqual);
  const allowCorrectiveRedemption = useSelector((state: IAppState) => R.includes(state.userState.role, state.resortToolsConfig.enableCorrectiveRedemption));
  const entitlementSharesError = useSelector((state: IAppState) => getErroredEntitlementShares(state, props.guest.masterGuestId), shallowEqual);
  const entitlementLoadError = useSelector((state: IAppState) => getError(state, props.guest.masterGuestId, ACTION_TYPE.GET_FNF_ENTITLEMENTS_REQUEST)?.errorMsg);
  const redeemedEntitlement = useSelector((state: IAppState) => state.redeemedEntitlement);
  const isRedeeming = useSelector((state: IAppState) => !!getLoading(state, props.guest.masterGuestId, ACTION_TYPE.REDEEM_ENTITLEMENT_REQUEST));
  const redemptionError = useSelector((state: IAppState) => getError(state, props.guest.masterGuestId, ACTION_TYPE.REDEEM_ENTITLEMENT_REQUEST));
  const loadingSubmitLocalRef = useSelector((state: IAppState) => !!getLoading(state, props.guest.masterGuestId, ACTION_TYPE.SUBMIT_LOCAL_REF_REQUEST));
  const errorSubmitLocalRef = useSelector((state: IAppState) => getError(state, props.guest.masterGuestId, ACTION_TYPE.SUBMIT_LOCAL_REF_REQUEST));
  // After repeated failures the breaker silently drops saves; show when saving resumes
  const localRefHeldUntil = useTimeAhead(useSelector((state: IAppState) => breakerHeldUntil(state, ACTION_TYPE.SUBMIT_LOCAL_REF_REQUEST)));
  const allEntitlementTypes = useSelector((state: IAppState) => state.entitlementTypesDetails?.allEntitlementTypes);
  const fnfBlackoutDateMetadata = useSelector((state: IAppState) => state.fnfBlackoutDateMetadata);
  const isLoadingFnFBlackoutDateMetadata = useSelector((state: IAppState) => !!getLoading(state, state.selectedResort.resortAbbreviation, ACTION_TYPE.GET_FNF_BLACKOUTDATE_METADATA_REQUEST));
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);
  const timeFormat = useSelector((state: IAppState) => state.defaultTimeFormat);
  const resources = useSelector((state: IAppState) => state.locale);

  const [currentPage, setCurrentPage] = useState(FNF_PAGES.ENTITLEMENTS);
  const [selectedEntitlement, setSelectedEntitlement] = useState<Entitlement | undefined>(undefined);
  const [redeemQuantity, setRedeemQuantity] = useState<number>(0);
  const [redeemDate, setRedeemDate] = useState<Date>(new Date());
  const [allDays, setAllDays] = useState('');
  const [allAvailable, setAllAvailable] = useState('');
  const [usedDays, setUsedDays] = useState('');
  const [blackoutDates, setBlackoutDates] = useState<Date[]>([]);
  const [datePickerMinDate, setDatePickerMinDate] = useState<Date | undefined>(undefined);
  const [datePickerMaxDate, setDatePickerMaxDate] = useState<Date | undefined>(undefined);
  const [datePickerBlackoutDates, setDatePickerBlackoutDates] = useState<DatePickerDateWithMessage[]>([]);

  const blackoutMessage = getResource(resources, 'FNF_TABLE_BLACKED_OUT');

  const findMaxExpirationDate = (arr: BlackoutDateMetadata[]) : Date | undefined => {
    if (arr.length === 0) return undefined;
    return arr.reduce((maxDate, m) => { 
      return new Date(m.expirationDate) > maxDate ? new Date(m.expirationDate) : maxDate; 
    }, new Date(arr[0].expirationDate));
  };

  useEffect(() => {
    if (selectedResortAbbreviation) {
      dispatch(getFnFBlackoutDateMetadataAction(selectedResortAbbreviation));
    }
  }, [selectedResortAbbreviation, dispatch]);

  useEffect(() => {

    if (!isLoadingFnFBlackoutDateMetadata 
      && fnfBlackoutDateMetadata 
      && fnfBlackoutDateMetadata.length > 0 
      && selectedEntitlement) {
        const metadata = fnfBlackoutDateMetadata.filter((m) => m.entitlementTypeId === selectedEntitlement.entitlementTypeId);
        if (metadata && metadata.length > 0) {
          const blackoutDates = getAllBlackoutDates(metadata);
          setBlackoutDates(blackoutDates);
          setDatePickerBlackoutDates(blackoutDates.map((date) => {
            return {date: date, message: blackoutMessage} as DatePickerDateWithMessage
          }));
          const max = findMaxExpirationDate(metadata)
          setDatePickerMinDate(new Date());
          setDatePickerMaxDate(max);
        }
    }
  }, [isLoadingFnFBlackoutDateMetadata, selectedEntitlement, fnfBlackoutDateMetadata, blackoutMessage]);

  const hotlistsRef = useRef(hotlists);
  useEffect(() => {
    hotlistsRef.current = hotlists;
  });

  useEffect(() => {
    if (props.entitlements.length && !props.isLoadingEntitlements) {
      const activeEntitlements = props.entitlements.filter(
        ent => isActive(ent) && !isDeferred(ent) && !fnfIsHotlisted(ent, props.guest, hotlistsRef.current)
      );
      if (activeEntitlements.length) {
        setAllDays(getAllDays(activeEntitlements));
        setAllAvailable(getAllAvailableDays(activeEntitlements));
        setUsedDays(getUsedDays(activeEntitlements));
      }
    }
  }, [props.isLoadingEntitlements, props.isLoadingEntitlementShares, isRedeeming, props.entitlements, props.guest]);

  const { setDisableClickoffAndKeypressModal } = props;

  useEffect(() => {
    const clearError = (error: ApiError) => dispatch(clearApiErrorAction(error.requestId, error.actionType));
    if (currentPage === FNF_PAGES.ENTITLEMENTS && redemptionError) {
      clearError(redemptionError!);
    }
    if (currentPage === FNF_PAGES.ENTITLEMENTS && errorSubmitLocalRef) {
      clearError(errorSubmitLocalRef);
    }

    setDisableClickoffAndKeypressModal((currentPage === FNF_PAGES.REDEEM_COMPLETION || currentPage === FNF_PAGES.RETURN_COMPLETION));
  }, [currentPage, redemptionError, errorSubmitLocalRef, setDisableClickoffAndKeypressModal, dispatch])

  const { getFnFEntitlements } = props;

  // Done only once seen loading, which starts just after the click
  // Clicks mid-request are ignored; the action drops them, and a reset would lose the answer
  const redemptionRequest = useRef<'idle' | 'requested' | 'inFlight'>('idle');
  const localRefRequest = useRef<'idle' | 'requested' | 'inFlight'>('idle');

  useEffect(() => { // This tracks when a redemption has been completed
    if (isRedeeming) {
      if (redemptionRequest.current === 'requested') redemptionRequest.current = 'inFlight';
      return;
    }
    if (redemptionRequest.current !== 'inFlight') return;

    redemptionRequest.current = 'idle';
    if (redemptionError) return; // FnFRedeemDays shows it

    if (currentPage === FNF_PAGES.REDEEM_DAYS) {
      setCurrentPage(FNF_PAGES.REDEEM_COMPLETION);
    } else if (currentPage === FNF_PAGES.RETURN_DAYS) {
      setCurrentPage(FNF_PAGES.RETURN_COMPLETION);
    }

    getFnFEntitlements();
  },[isRedeeming, currentPage, redemptionError, getFnFEntitlements])

  useEffect(() => { // This tracks when the local ref has been submitted to update
    if (loadingSubmitLocalRef) {
      if (localRefRequest.current === 'requested') localRefRequest.current = 'inFlight';
      return;
    }
    if (localRefRequest.current !== 'inFlight') return;

    localRefRequest.current = 'idle';
    if (errorSubmitLocalRef) return;

    setCurrentPage(FNF_PAGES.ENTITLEMENTS);
    getFnFEntitlements();
  },[loadingSubmitLocalRef, getFnFEntitlements, errorSubmitLocalRef])


  const redeemEntitlement = (redeemQuantity: number, redeemDate: Date) => {
    if (redemptionRequest.current === 'inFlight') return;

    setRedeemQuantity(redeemQuantity);
    setRedeemDate(redeemDate);
    redemptionRequest.current = 'requested';
    dispatch(redeemEntitlementAction(
      {
        guestId: selectedEntitlement!.masterGuestId,
        entitlementId: selectedEntitlement!.entitlementId,
        redemptionDate: redeemDate,
        redemptionQuantity: redeemQuantity,
      }
    ));
  }

  const handleSubmitRef = (refId: string, redemptionId: string | undefined) => {
    if (!selectedEntitlement || !redemptionId || localRefRequest.current === 'inFlight') {
      return;
    }

    const redemption : Redemption = {
      entitlementId: redeemedEntitlement?.entitlementId || selectedEntitlement.entitlementId,
      guestId: props.guest.masterGuestId,
      redemptionDate: redeemDate,
      redemptionId: redemptionId || '',
      localRef: refId,
      redemptionQuantity: redeemedEntitlement?.redemptionQuantity || redeemQuantity,
    }

    localRefRequest.current = 'requested';
    dispatch(submitRedemptionLocalRefAction(redemption, props.guest));
  }


  const renderEntitlementPage = () => {
    return (
      <div css={css`${COLUMN_SPACING}`}>   
        <LabeledData label={getResource(resources, 'FNF_HISTORY_GUEST_LABEL')} data={fullName(props.guest)} />
        { // Loading entitlements
          props.isLoadingEntitlements
          &&  <div css={css`display: flex; width: 100%; justify-content: center`}>
            <Loader
              rootCss={styles.fnfLoader}
            />
          </div>
        }
        
        <ErrorView
          error={
            !props.isLoadingEntitlements && !isNilOrEmpty(entitlementLoadError)
              ? getResource(resources, 'FNF_MODAL_FAILED_FETCH', apostrophize(fullName(props.guest)))
              : undefined
          }
        />
        
        { // Redemption Summary
          !props.isLoadingEntitlements && 
          !entitlementLoadError &&
          <RedemptionDetails
            totalDays={allDays}
            daysUsed={usedDays}
            remainingDaysAvailable={allAvailable}
            disableTooltip={true}
          />
        }

        <FnFEntitlementsTable
          entitlements={props.entitlements}
          guest={props.guest}
          allowCorrectiveRedemption={allowCorrectiveRedemption}
          getEntitlementShares={props.getEntitlementShares}
          getFnFEntitlements={props.getFnFEntitlements}
          setCurrentPage={setCurrentPage}
          setSelectedEntitlement={setSelectedEntitlement}
          entitlementShares={props.entitlementShares || []}
          isLoadingEntitlementShares={props.isLoadingEntitlementShares}
          entitlementSharesError={!!entitlementSharesError}
        />
      </div> 
    )
  }

  const renderRedeemOrReturnDaysPage = (entitlement: Entitlement) => {
    return (
      <FnFRedeemDays
        guest={props.guest}
        entitlement={entitlement}
        allowCorrectiveRedemption={allowCorrectiveRedemption}
        allEntitlementTypes={allEntitlementTypes}
        blackoutDates={blackoutDates}
        datePickerBlackoutDates={datePickerBlackoutDates}
        datePickerMinDate={datePickerMinDate}
        datePickerMaxDate={datePickerMaxDate}
        resources={resources}
        dateFormat={dateFormat}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        redeemEntitlement={redeemEntitlement}
        redemptionError={redemptionError?.errorMsg}
      />
    )
  }

  const localRefError = localRefHeldUntil !== undefined
    ? getResource(resources, 'FNF_MODAL_LOCAL_REF_HELD', formatTime(shownReleaseTime(localRefHeldUntil), timeFormat))
    : errorSubmitLocalRef ? getResource(resources, 'FNF_MODAL_FAILED_LOCAL_REF') : undefined;

  const renderCompletionPage = (title: string) => {
    return (
      <div css={css`${COLUMN_SPACING};`}>
        <span css={styles.redemptionComplete}>
          <img src={checkmark} alt='✓' css={css`margin-right: 15px;`}/>
          {title}
        </span>
        <RedemptionIdForm
          resources={resources}
          redemptionId={redeemedEntitlement?.redemptionId}
          onLocalRefSave={handleSubmitRef}
        >
          {/* Hidden rather than removed so Save & Close doesn't shift */}
          <StyledBoxedMessage
            message={localRefError ?? ' '}
            extraCss={localRefError ? undefined : css`visibility: hidden;`}
          />
        </RedemptionIdForm>
      </div>
    )
  }
  


return (
  <div>
    {
      currentPage === FNF_PAGES.ENTITLEMENTS && 
      renderEntitlementPage()
    }
    {
      (currentPage === FNF_PAGES.RETURN_DAYS || currentPage === FNF_PAGES.REDEEM_DAYS) && 
      renderRedeemOrReturnDaysPage(selectedEntitlement!)
    }
    {
      currentPage === FNF_PAGES.REDEEM_COMPLETION &&
      renderCompletionPage(getResource(resources, 'FNF_MODAL_REDEMPTION_COMPLETE'))
    }
    {
      currentPage === FNF_PAGES.RETURN_COMPLETION &&
      renderCompletionPage(getResource(resources, 'FNF_MODAL_RETURN_COMPLETE'))
    }
  </div>
)};
